/**
 * Chain Field Policy
 * Defines default fields and zero/blank handling policies for option chains
 */

import { OPTION_ENTITY_V1_FIELDS } from "../entities/optionEntity.schema";

/**
 * Default field keys to include in chain inserts
 * Must be actual keys from OPTION_ENTITY_V1_FIELDS
 */
export const DEFAULT_CHAIN_FIELD_KEYS: string[] = [
  "bid",
  "ask",
  "mid",
  "last",
  "iv",
  "delta",
  "gamma",
  "theta",
  "vega",
  "volume",
  "open_interest",
];

/**
 * Validate that DEFAULT_CHAIN_FIELD_KEYS only contains valid v1 fields
 * Throws in dev mode if invalid keys are found
 */
function validateDefaultFieldKeys(): void {
  const invalidKeys = DEFAULT_CHAIN_FIELD_KEYS.filter(
    key => !OPTION_ENTITY_V1_FIELDS.includes(key as any)
  );
  
  if (invalidKeys.length > 0) {
    const message = `DEFAULT_CHAIN_FIELD_KEYS contains invalid keys: ${invalidKeys.join(", ")}. Valid keys: ${OPTION_ENTITY_V1_FIELDS.join(", ")}`;
    if (process.env.NODE_ENV !== "production") {
      throw new Error(message);
    }
    // In production, log warning but don't throw
    console.warn("[chainFields] " + message);
  }
}

// Validate at module load
validateDefaultFieldKeys();

/**
 * Fields where missing values should default to 0
 * These are count fields where 0 is a valid value
 */
export const ZERO_DEFAULT_FIELDS = new Set<string>([
  "open_interest",
  "volume",
]);

/**
 * Fields where missing values should stay blank (not 0)
 * These are price/greek fields where missing data should remain empty
 * Note: True zeros are preserved (if value is 0, it stays 0)
 * 
 * Future consideration: For market price fields (bid/ask), we may treat 0 as blank
 * since real markets don't have 0 prices. If Massive emits 0 for missing prices,
 * consider: `=IF(OR(${propRef}="",${propRef}=0),"",...)`
 */
export const BLANK_DEFAULT_FIELDS = new Set<string>([
  "bid",
  "ask",
  "mid",
  "last",
  "iv",
  "delta",
  "gamma",
  "theta",
  "vega",
  "rho",
  "change",
  "change_percent",
  "strike",
]);
